// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Formatting utilities for legislative review.
 */

/**
 * Format ISO date string to readable format.
 */
export function formatDate(isoString: string | null | undefined): string {
  if (!isoString) return '-'
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const BILL_TYPE_LABELS: Record<string, string> = {
  hr: 'House Bills',
  s: 'Senate Bills',
  hjres: 'House Joint Resolutions',
  sjres: 'Senate Joint Resolutions',
  hconres: 'House Concurrent Resolutions',
  sconres: 'Senate Concurrent Resolutions',
  hres: 'House Resolutions',
  sres: 'Senate Resolutions',
}

const ORDINAL_SUFFIXES: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' }

function toOrdinal(n: number): string {
  const lastTwo = n % 100
  if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`
  return `${n}${ORDINAL_SUFFIXES[n % 10] || 'th'}`
}

/**
 * Convert session code to readable format.
 * e.g., "119-hr" -> "119th Congress — House Bills"
 * e.g., "2024RS" -> "2024 Regular Session"
 */
export function formatSessionCode(code: string): string {
  const federalMatch = code.match(/^(\d+)-(\w+)$/)
  if (federalMatch) {
    const congress = parseInt(federalMatch[1], 10)
    const billType = federalMatch[2].toLowerCase()
    const label = BILL_TYPE_LABELS[billType] || billType.toUpperCase()
    return `${toOrdinal(congress)} Congress — ${label}`
  }
  const stateMatch = code.match(/^(\d{4})RS$/)
  if (stateMatch) {
    return `${stateMatch[1]} Regular Session`
  }
  return code
}
